<template>
  <div>
    <template v-for="field in schema">
      <v-text-field
        class="mt-0 mb-4"
        v-if="[availableFields.text, availableFields.integer].includes(field.type)"
        :type="availableFields.integer === field.type ? 'number' : 'text'"
        :label="field.title"
        :value="value[field.name]"
        :key="field.uuid"
        :error-messages="errorMessages[field.name]"
        :readonly="readonly"
        :disabled="readonly"
        @input="updateField(field.name, $event, field)"
      />
      <v-textarea
        class="mt-2 mb-2"
        outlined
        v-if="field.type === availableFields.textarea"
        :label="field.title"
        :value="value[field.name]"
        :key="field.uuid"
        :error-messages="errorMessages[field.name]"
        :readonly="readonly"
        :disabled="readonly"
        @input="updateField(field.name, $event, field)"
      />

      <template v-if="field.type === availableFields.radio">
        <v-radio-group
          v-if="Array.isArray(field.options) && field.options.length > 0"
          :key="field.uuid"
          column
          :readonly="readonly"
          :disabled="readonly"
          :error-messages="errorMessages[field.name]"
          :value="value[field.name]"
          @change="updateField(field.name, $event, field)"
        >
          <template #label>
            <div>{{ field.title }}</div>
          </template>

          <v-radio
            v-for="(radio, ridx) in field.options"
            class="my-1"
            outlined
            :name="field.name"
            :label="radio.label"
            :value="radio.value"
            :key="`${field.uuid}-${ridx}`"
          />
        </v-radio-group>
        <div v-else :key="field.uuid" class="my-2">
          <div class="mb-1">
            <strong>{{ field.title }}</strong>
          </div>
          <v-alert dense outlined type="error">
            Для поля не заполнены варианты значений в админ панели. Обратитесь к администрации.
          </v-alert>
        </div>
      </template>

      <template v-if="field.type === availableFields.select">
        <v-select
          v-if="Array.isArray(field.options) && field.options.length > 0"
          :key="field.uuid"
          outline
          class="mt-2 mb-2"
          item-text="label"
          item-value="value"
          :label="field.title"
          :readonly="readonly"
          :multiple="field.multi"
          :disabled="readonly"
          :items="field.options"
          :error-messages="errorMessages[field.name]"
          :value="getProperSelectValue(value[field.name], field.multi, field.options[0]['value'])"
          @change="updateField(field.name, $event, field)"
        />
        <div v-else :key="field.uuid" class="my-2">
          <div class="mb-1">
            <strong>{{ field.title }}</strong>
          </div>
          <v-alert dense outlined type="error">
            Для поля не заполнены варианты значений в админ панели. Обратитесь к администрации.
          </v-alert>
        </div>
      </template>

      <date-picker
        class="mt-0 mb-4"
        v-if="field.type === availableFields.datePicker"
        :label="field.title"
        :value="value[field.name]"
        :key="field.uuid"
        :error-messages="errorMessages[field.name]"
        :readonly="readonly"
        :disabled="readonly"
        :max="field.name === 'real_date' ? new Date().toISOString() : null"
        @input="updateField(field.name, $event, field)"
      />

      <base-input-file
        v-if="[availableFields.file, availableFields.dicom].includes(field.type)"
        show-size
        counter
        small-chips
        :key="field.uuid"
        :label="field.title"
        :disabled="readonly"
        :multiple="!!field.multi"
        :error-messages="errorMessages[field.name]"
        :value="value[field.name]"
        @change="updateField(field.name, $event, field)"
        @click:file="fileClick"
        @file-remove="onFileRemove(field.name, $event)"
      />

      <template v-if="field && field.type === availableFields.source">
        <v-autocomplete
          hide-no-data
          item-text="title"
          item-value="id"
          cache-items
          :label="field.title"
          :value="getProperSelectValue(value[field.name], field.multi, field.items[0] ? field.items[0]['id'] : null)"
          :key="field.uuid + '-' + value[field.name]"
          :error-messages="errorMessages[field.name]"
          :readonly="readonly"
          :disabled="readonly"
          :multiple="field.multi"
          :deletable-chips="field.multi"
          :small-chips="field.multi"
          :items="[...field.items, ...(field.addedItems || [])]"
          @change="updateField(field.name, $event, field)"
          @update:search-input="sourceSearch($event, field)"
        />
        <v-btn
          v-if="!readonly && field.source === 'icd10'"
          depressed
          color="primary"
          type="button"
          small
          :key="field.uuid + '-selector'"
          @click="openMKB10modal(field)"
        >
          Выбрать из списка МКБ-10
        </v-btn>
      </template>

      <v-text-field
        class="mt-0 mb-4 filed-type-param"
        v-if="field.type === availableFields.param"
        :type="field.behavior && field.behavior.html_input_type ? field.behavior.html_input_type : 'text'"
        :step="field.behavior && field.behavior.html_input_type && field.behavior.html_input_type === 'number' ? 0.1 : null"
        persistent-hint
        :label="field.title"
        :hint="field.options ? `Параметр: ${field.options.param_name}, \n Ед. измерения: ${field.options.measure_name}` : ''"
        :value="value[field.name]"
        :key="field.uuid"
        :error-messages="errorMessages[field.name]"
        :readonly="readonly"
        :disabled="readonly"
        @input="updateField(field.name, $event, field)"
      />

      <div v-if="field.type === availableFields.params" :key="field.uuid">
        <v-card outlined>
          <v-card-text>
            <div class="d-flex align-center justify-space-between">
              <h4 class="card-title font-weight-light">Параметры</h4>
              <v-btn v-if="!readonly" class="mr-0" color="primary" @click="addParam(field.name)">Добавить</v-btn>
            </div>
          </v-card-text>

          <div class="pb-2 pl-4 error--text" v-if="errorMessages[field.name] && errorMessages[field.name].length">
            {{ errorMessages[field.name].join(", ") }}
          </div>

          <v-simple-table dense v-if="Array.isArray(value[field.name]) && value[field.name].length">
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-left">Название параметра</th>
                  <th class="text-left">Значение</th>
                  <th class="text-left">Норма</th>
                  <th v-if="!readonly" class="text-left"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in value[field.name]" :key="item.uuid">
                  <td title="Параметр">
                    <div v-if="item.edit && !readonly">
                      <v-autocomplete
                        label="Выберите параметр"
                        hide-no-data
                        item-text="name"
                        return-object
                        required
                        v-model="item.param"
                        :items="metrics"
                        @change="loadMeasures(item)"
                      />
                    </div>
                    <div v-else>{{ item.param.name }}</div>
                  </td>
                  <td title="Количество" width="100px">
                    <div v-if="item.edit && !readonly">
                      <v-text-field label="Кол-во" type="number" required v-model.number="item.value" />
                    </div>
                    <div v-else>{{ item.value }} {{ item.measure.name }}</div>
                  </td>
                  <td :title="item.edit ? 'Единица измерения' : 'Норма'">
                    <div v-if="item.edit && !readonly">
                      <v-autocomplete
                        label="Ед. изм."
                        hide-no-data
                        item-text="name"
                        return-object
                        required
                        v-model="item.measure"
                        :loading="item.measuresLoading"
                        :items="item.measures"
                      />
                    </div>
                    <div v-else>
                      <med-param-norm :value="item.value" :min="item.measure.min" :max="item.measure.max" />
                    </div>
                  </td>
                  <td v-if="!readonly">
                    <div class="d-flex flex-row justify-center align-center">
                      <v-icon
                        title="Сохранить"
                        class="mr-2"
                        color="primary"
                        small
                        v-if="item.edit"
                        :disabled="!item.param.name || item.value === null || item.value === undefined || !item.measure"
                        @click="saveParam(field.name, item)"
                      >
                        save
                      </v-icon>

                      <v-icon
                        title="Редактировать"
                        class="mr-2"
                        color="primary"
                        small
                        v-if="!item.edit"
                        @click="item.edit = true"
                      >
                        mdi-pencil
                      </v-icon>

                      <v-icon title="Удалить" color="primary" small @click="deleteParam(field.name, item)">
                        mdi-delete
                      </v-icon>
                    </div>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card>
      </div>
      <div v-if="field.type === availableFields.paramnorms" :key="field.uuid">
        <v-card outlined>
          <v-card-text>
            <div class="d-flex align-center justify-space-between">
              <h4 class="card-title font-weight-light">Индивидуальные нормы параметров</h4>
              <v-btn v-if="!readonly" class="mr-0" color="primary" @click="addParam(field.name)">Добавить</v-btn>
            </div>
          </v-card-text>

          <div class="pb-2 pl-4 error--text" v-if="errorMessages[field.name] && errorMessages[field.name].length">
            {{ errorMessages[field.name].join(", ") }}
          </div>
          <v-simple-table dense v-if="Array.isArray(value[field.name]) && value[field.name].length">
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-left">Название параметра</th>
                  <th class="text-left">Минимум</th>
                  <th class="text-left">Максимум</th>
                  <th v-if="!readonly" class="text-left"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in value[field.name]" :key="item.uuid">
                  <td title="Параметр">
                    <div v-if="item.edit && !readonly">
                      <v-autocomplete
                        label="Выберите параметр"
                        hide-no-data
                        item-text="name"
                        return-object
                        required
                        v-model="item.param"
                        :items="metrics"
                        @change="loadMeasures(item)"
                      />
                    </div>
                    <div v-else>{{ item.param.name }}</div>
                  </td>
                  <td title="Минимум" width="100px">
                    <div v-if="item.edit && !readonly">
                      <v-text-field label="Минимум" type="number" required v-model.number="item.min_value" />
                    </div>
                    <div v-else>{{ item.min_value }}</div>
                  </td>
                  <td title="Максимум" width="100px">
                    <div v-if="item.edit && !readonly">
                      <v-text-field label="Максимум" type="number" required v-model.number="item.max_value" />
                    </div>
                    <div v-else>{{ item.max_value }}</div>
                  </td>
                  <td :title="item.edit ? 'Единица измерения' : 'Норма'">
                    <div v-if="item.edit && !readonly">
                      <v-autocomplete
                        label="Ед. изм."
                        hide-no-data
                        item-text="name"
                        return-object
                        required
                        v-model="item.measure"
                        :loading="item.measuresLoading"
                        :items="item.measures"
                      />
                    </div>
                    <div v-else>
                      {{ item.measure.name }}
                    </div>
                  </td>
                  <td v-if="!readonly">
                    <div class="d-flex flex-row justify-center align-center">
                      <v-icon
                        title="Сохранить"
                        class="mr-2"
                        color="primary"
                        small
                        v-if="item.edit"
                        :disabled="!item.param.name || item.value === null || item.value === undefined || !item.measure"
                        @click="saveParam(field.name, item)"
                      >
                        save
                      </v-icon>

                      <v-icon
                        title="Редактировать"
                        class="mr-2"
                        color="primary"
                        small
                        v-if="!item.edit"
                        @click="item.edit = true"
                      >
                        mdi-pencil
                      </v-icon>

                      <v-icon title="Удалить" color="primary" small @click="deleteParam(field.name, item)">
                        mdi-delete
                      </v-icon>
                    </div>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card>
      </div>

      <BoolGroup
        class="mt-0 mb-4 filed-type-param"
        v-if="field.type === availableFields.bool"
        :label="field.title"
        :value="value[field.name]"
        :key="field.uuid"
        :error-messages="errorMessages[field.name]"
        :disabled="readonly"
        @input="updateField(field.name, $event, field)"
      />
    </template>

    <v-divider class="mt-4"></v-divider>

    <base-input-file
      label="Прикрепленные файлы"
      multiple
      :disabled="readonly"
      :value="value['attach_files']"
      @change="updateField('attach_files', $event)"
      @click:file="fileClick"
      @view:file="fileView"
      @file-remove="onFileRemove('attach_files', $event)"
    />
  </div>
</template>

<script>
import { uniqueId } from "lodash/util";
import DatePicker from "@/components/forms/DatePicker.vue";
import { ParamsService } from "@/services/api/params.service.js";
import MedParamNorm from "@/components/MedParamNorm.vue";
import BaseInputFile from "@/components/forms/BaseInputFile.vue";
import { buildDocumentUrl, downloadFile, fileReader } from "@/services/utils.js";
import { DOCUMENT_AVAILABLE_FIELDS } from "@/modules/documents/add-document/document-available-fields.js";
import BoolGroup from "@/modules/documents/add-document/BoolGroup.vue";
import SearchMKB10 from "@/components/SearchMKB10";
import ViewAttachmentModal from "./ViewAttachmentModal.vue";
export default {
  name: "DynamicForm",

  components: { BoolGroup, BaseInputFile, MedParamNorm, DatePicker, SearchMKB10 },

  props: {
    readonly: { type: Boolean, default: false },
    showMode: { type: Boolean, default: false },
    schema: { type: Array, required: true },
    validation: { type: Object, required: true },
    value: { type: Object, required: true },
  },

  data: () => ({
    hasParamField: false,
    availableFields: DOCUMENT_AVAILABLE_FIELDS,
    metrics: [],
  }),

  watch: {
    "value.category"(val, oldVal) {
      if (val !== oldVal) {
        this.fillDefaults();
      }
    },
    schema: {
      // При изменении типов полей будем проверять и загружать дополнительные вспомогательные данные
      handler: function(schema) {
        // Если данные мед. параметров еще не загружены, тогда проверяем или нужно их загрузить.
        if (!this.metrics.length) {
          schema.forEach(field => {
            // Проверяем нет ли в массиве полей поля ПАРАМЕТРЫ
            if ([DOCUMENT_AVAILABLE_FIELDS.params, DOCUMENT_AVAILABLE_FIELDS.paramnorms].includes(field.type)) {
              ParamsService.getAll().then(data => {
                this.metrics = data;
              });
            }
          });
        }
      },
      immediate: true,
    },
  },

  computed: {
    customerId() {
      return this.$store.state.customer.id;
    },

    errorMessages() {
      return this.schema.reduce((messages, field) => {
        if (field.required) {
          const validator = this.validation[field.name];

          if (!validator) return messages;
          if (!validator.$error) return messages;

          messages[field.name] = ["Обязательно для заполнения"];
        }

        return messages;
      }, {});
    },
  },

  methods: {
    getProperSelectValue(value, multi, firstValue) {
      let result = value;

      if (Array.isArray(value)) {
        if (typeof firstValue === "number") {
          return multi ? result.map(Number) : Number(result[0]);
        }
        return multi ? result : result[0];
      }
      // у селектов почему то приходит значение в виде строки с массивом
      if (typeof value === "string" && Array.isArray(JSON.parse(value))) {
        if (typeof firstValue === "number") {
          return multi ? JSON.parse(value).map(Number) : Number(JSON.parse(value)[0]);
        }
        return multi ? JSON.parse(value) : JSON.parse(value)[0];
      }
      return result;
    },
    /**
     * Хендлер для обновление полей.
     * При изменении любого из полей метод пересобирает объект формы и эмитит наружу измененный объект.
     */
    updateField(key, value, field = null) {
      if (this.readonly) {
        return;
      }

      let val = value;

      // Проверяем на тип поля FILE, если поле multi=0 тогда поле типа файл не должно быть массивом
      if (
        field !== null &&
        [this.availableFields.file, this.availableFields.dicom].includes(field.type) &&
        field.multi === 0 &&
        Array.isArray(value)
      ) {
        val = value[0];
      }

      // Проверяем на тип поля SELECT, должно быть массивом
      if (field !== null && field.type === this.availableFields.select && !Array.isArray(value)) {
        val = [val];
      }
      this.$emit("input", { ...this.value, [key]: val });
    },

    /**
     * Добавляет новый параметр в форму
     */
    addParam(fieldName) {
      const params = Array.isArray(this.value[fieldName]) ? [...this.value[fieldName]] : [];

      params.push({
        uuid: uniqueId(),
        edit: true,
        measuresLoading: false,
        measures: [],

        param: "",
        value: "",
        measure: "",
      });

      this.$emit("input", { ...this.value, [fieldName]: params });
    },

    /**
     * Загружает варианты "Единиц измерений" для мед параметра
     */
    async loadMeasures(param) {
      param.measuresLoading = true;
      param.measures = await ParamsService.getMeasure(param.param.id);
      param.measuresLoading = false;

      if (param.measures.length === 1) {
        param.measure = param.measures[0];
      }
    },

    saveParam(formField, param) {
      param.edit = false;
    },

    deleteParam(formField, param) {
      this.$emit("input", {
        ...this.value,
        [formField]: this.value[formField].filter(item => item.uuid !== param.uuid),
      });
    },

    fileClick(file) {
      // Если file является типом File, тогда этого только добавленный файл в форму которого еще нет на сервере
      if (file instanceof File) {
        return;
      }

      this.$http
        .get(buildDocumentUrl(this.customerId, file.id), { responseType: "blob" })
        .then(response => {
          downloadFile(file.name, response.data);
        })
        .catch(async err => {
          const file = await fileReader(err.response.data);
          const { message } = JSON.parse(file);

          await this.$dialog.notify.error(message ?? "Ошибка получения файла");
        });
    },
    fileView(file) {
      this.$dialog.showAndWait(ViewAttachmentModal, { width: "600px", file });
    },
    onFileRemove(field, file) {
      this.$emit("file-remove", { field, file });
    },

    // Поиск по динамическим справочникам
    sourceSearch($event, field) {
      // если это режим чтения, тогда выходим из функции
      if (this.readonly) {
        return;
      }

      // Если не searchable то выходим из поиск
      if (!field.behavior || field.behavior.searchable === false) {
        return;
      }

      // Нужно вспомнить зачем я тут стирал данные
      if (!$event) {
        // field.items = [];
        return;
      }

      this.$emit("source-search", { value: $event, field });
    },
    async openMKB10modal(field) {
      const res = await this.$dialog.showAndWait(SearchMKB10, { width: "600px", asModal: true });

      if (res) {
        if (!field.items.length || field.multi) {
          if (!res.title && res.name) {
            res.title = res.name;
          }
          field.items.push(res);
        }

        let value = res.id;
        if (field.multi) {
          value = field.items.map(el => {
            return el.id;
          });
        }
        this.updateField(field.name, value, field);
      }
    },

    getFieldByName(name) {
      return this.schema.find(el => el.name === name);
    },

    async fillDefaults() {
      if (this.value.category && this.value.category.name) {
        const nameField = this.getFieldByName("name");
        if (nameField) {
          this.updateField("name", this.value.category.name, nameField);
          await this.$nextTick();
        }
      }

      const dateField = this.getFieldByName("real_date");
      if (dateField) {
        this.updateField("real_date", this.$date().format("YYYY-MM-DD"), dateField);
        await this.$nextTick();
      }

      const ANAMNESIS_VITAE = this.getFieldByName("field_313");

      if (ANAMNESIS_VITAE && !this.value["field_313"]) {
        this.updateField(
          "field_313",
          `
Контакт с инфекционными больными, выезд за границу - отрицает.
Принесенные заболевания: Гепатит - отриц. ВИЧ – инфекцию - отриц. Венерические заб-я - отриц.
Туберкулез - отриц. На учете не состоит.
Заболевания:
ИБС – отриц.
Артериальная гипертензия – отриц.
Сахарный диабет - отриц.
Язвенная болезнь – отриц.
Мочекаменная болезнь - отриц.
ЖКБ, хронический холецистит – отриц.
Хронический бронхит - отриц.
Другие заболевания – отриц.
Вредные привычки: курение: отриц, алкоголь: отриц.
Операции – отриц.
Аллергоанамнез не отягощен- отрицает, реакция-слезотечение - отриц
Гемотрансфузии: отриц.
Заболевания родственников (АГ, ИБС, сахарный диабет, язвенная болезнь, туберкулез, онкозаболевания) указать не может.
Гинекологический анамнез: нет
Принимаемые лекарственные препараты: отриц.
`,
          ANAMNESIS_VITAE,
        );
        await this.$nextTick();
      }

      const objective_status = this.getFieldByName("field_314");
      if (objective_status && !this.value["field_314"]) {
        this.updateField(
          "field_314",
          `
Общее состояние: удовлетворительное
Сознание: ясное
Положение тела: активное
Телосложение: правильное
Подкожно-жировая клетчатка выражена: не выражена
Температура тела: 36,6 С
Кожные покровы: физиологической окраски, влажные
Слизистая ротовой полости: без особенностей
Миндалины: не увеличены
Конъюктива: чистая
Периферические лимфоузлы: не увеличены
Сердечно-сосудистая система: Границы сердца в норме, тоны сердца ясные, правильного ритма
Артериальное давление: ___ мм рт ст. на левой руке. ___ мм рт. ст. на правой руке. Пульс ___ в мин, правильного ритма, удовлетворительного наполнения и напряжения
Дыхательная система: грудная клетка правильной формы, равномерно участвует в акте дыхания
Перкуторный звук: легочный, проводится во все отделы
Границы легких: в норме
При аускультации дыхание: везикулярное, хрипов нет
Пищеварительная система: язык чистый, влажный
Живот при пальпации: правильной формы, участвует в акте дыхания, мягкий, безболезненный
Симптом раздражения брюшины:
Печень: не увеличена
Селезенка: не увеличена
Мочевыделительная система: поколачивание по поясничной области безболезненное. Мочеиспускание свободное
Дополнительные данные:
Принимаемые препараты:
`,
          objective_status,
        );
        await this.$nextTick();
      }

      const recommendations = this.getFieldByName("field_322");
      if (recommendations && !this.value["field_322"]) {
        this.updateField(
          "field_322",
          `
-ОАК
-ОАМ
-ЭКГ в покое
`,
          recommendations,
        );
        await this.$nextTick();
      }
    },
  },

  mounted() {
    this.fillDefaults();
  },
};
</script>

<style scoped>
.filed-type-param /deep/ .v-messages__message {
  white-space: pre-line;
}
</style>
